interface ImageValidationOptions {
  fallbackUrl: string;
  timeoutMs?: number;
  retries?: number;
  signal?: AbortSignal;
}

/**
 * Helper function to load a single image with timeout
 */
const loadImageWithTimeout = (
  imgUrl: string,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if already aborted
    if (signal?.aborted) {
      return resolve(false);
    }

    const img = new Image();
    let isResolved = false;

    const cleanup = () => {
      if (!isResolved) {
        isResolved = true;
        clearTimeout(timer);
        img.onload = null;
        img.onerror = null;
      }
    };

    // Handle abort signal
    const onAbort = () => {
      cleanup();
      resolve(false);
    };

    if (signal) {
      signal.addEventListener('abort', onAbort);
    }

    // If image loads successfully
    img.onload = () => {
      if (!isResolved) {
        cleanup();
        if (signal) {
          signal.removeEventListener('abort', onAbort);
        }
        resolve(true);
      }
    };

    // If image fails to load (404, permission denied, etc.)
    img.onerror = () => {
      if (!isResolved) {
        cleanup();
        if (signal) {
          signal.removeEventListener('abort', onAbort);
        }
        resolve(false);
      }
    };

    // Safety timeout (for hanging requests)
    const timer = setTimeout(() => {
      if (!isResolved) {
        cleanup();
        if (signal) {
          signal.removeEventListener('abort', onAbort);
        }
        resolve(false);
      }
    }, timeoutMs);

    // Start loading
    img.src = imgUrl;
  });
};

/**
 * Helper function to calculate exponential backoff delay with cap
 * Caps at 30 seconds to prevent excessive delays
 */
const calculateBackoffDelay = (attemptNumber: number): number => {
  // 500ms, 1s, 2s, 4s, 8s, 16s, 30s (capped)
  const delay = 500 * Math.pow(2, attemptNumber);
  const MAX_DELAY = 30000; // 30 seconds
  return Math.min(delay, MAX_DELAY);
};

/**
 * Helper function to wait for a specified duration (with abort support)
 */
const delay = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      return resolve();
    }

    const timer = setTimeout(resolve, ms);

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timer);
        resolve();
      });
    }
  });
};

/**
 * Validates an image URL with retry logic and exponential backoff.
 * 
 * @param imgUrl - The image URL to validate
 * @param options - Configuration options
 * @param options.fallbackUrl - URL to use if validation fails
 * @param options.timeoutMs - Timeout for each attempt (default: 5000ms)
 * @param options.retries - Number of retry attempts (default: Infinity for infinite retries)
 * @param options.signal - AbortSignal for cancellation support
 * @returns Promise resolving to validated URL or fallback URL
 */
export const validateImageUrl = async (
  imgUrl: string | null | undefined,
  options: ImageValidationOptions
): Promise<string> => {
  const {
    fallbackUrl,
    timeoutMs = 5000,
    retries = Infinity,
    signal
  } = options;

  // Handle empty or invalid string
  if (!imgUrl || imgUrl.trim() === "") {
    return fallbackUrl;
  }

  // Check if already aborted
  if (signal?.aborted) {
    return fallbackUrl;
  }

  let lastError: string | null = null;
  let attempt = 0;

  // Attempt to load the image with retries (infinite by default)
  while (attempt < retries) {
    try {
      // Check if aborted before each attempt
      if (signal?.aborted) {
        return fallbackUrl;
      }

      const success = await loadImageWithTimeout(imgUrl, timeoutMs, signal);

      if (success) {
        return imgUrl;
      }

      lastError = `Attempt ${attempt + 1} failed`;

      // Wait before retrying (always, since we retry infinitely or until success)
      const backoffDelay = calculateBackoffDelay(attempt);
      await delay(backoffDelay, signal);
      
      attempt++;
    } catch (error) {
      lastError = error instanceof Error ? error.message : 'Unknown error';
      
      // Wait before retrying
      const backoffDelay = calculateBackoffDelay(attempt);
      await delay(backoffDelay, signal);
      
      attempt++;
    }
  }

  // This will only be reached if retries is not Infinity
  // Log error for monitoring (optional)
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[Image Validation] Failed to load image after ${retries} attempts:`, {
      url: imgUrl,
      lastError,
      fallbackUsed: fallbackUrl
    });
  }

  return fallbackUrl;
};